import { Subscription } from 'rxjs';
import { EwsRefresh } from '../../../../../services/ews-refresh';
import { simulateEws, EwsSimulation } from '../../../../../services/ews-simulation';
import { OnDestroy, OnChanges, SimpleChanges, Component, ChangeDetectorRef, Inject, Input, OnInit, Optional } from '@angular/core';
import { lastValueFrom, RmsMessageService } from '@services';
import { RmsModule } from '@rms';
import { Column, HeaderButton, TableIcon } from '@interfaces';
import {
  EwsDataService, EwsFilters, EwsGroupAnalysisResult, ForecastLine, dateOnly, monthLabel
} from '../../../../../services/ews-data.service';
import { downloadCsv } from '../../../../../services/swap-data.service';
import { RmsPulldownRef } from '../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown-ref';
import { RMS_PULLDOWN_DATA } from '../../../../../custom-modules/rms-module/rms-pulldown/rms-pulldown.tokens';

// =====================================================================
// GroupAnalysisComponent — Scope 6.4 EWS Group Analysis (S2)
// ---------------------------------------------------------------------
// Opened two ways:
//   1. as a pulldown from the EWS Summary (click a coffee group) — data comes
//      through RMS_PULLDOWN_DATA;
//   2. embedded in the "EWS Group Analysis" screen (C2.2) — data comes through
//      the [groupIds] / [filters] inputs.  The page re-creates this component
//      on every "View Report", so the inputs are read once in ngOnInit.
// C2.1 — laid out like the legacy "RNY EWS Report":
//   navy header band (crop #1 / crop #2 purchases, export window, days to
//   INSTORE, spot & forward depletion) · warehouse picker · chart (bags left
//   per month + yellow arrivals line + new-crop line with its value) next to
//   the Top 15 bag burners · month-by-month summary details · tabs:
//   Customers (✗ exclude), SKUs (✗ exclude / ✓ include, grand totals),
//   Arrivals, Freight, Burn History (monthly rate), Notes.
// Exclusions / inclusions re-run the analysis (legacy behaviour).
// Data: GET /api/v1/ews/group-analysis  (proc ews_get_group_analysis)
// =====================================================================

export interface GroupAnalysisData {
  GroupIDs: number[];
  Filters: EwsFilters;
}

interface ChartBar {
  x: number; y: number; w: number; h: number;
  value: number; label: string; short: boolean; crop: boolean; cropLabel: string;
}

@Component({
  selector: 'app-group-analysis',
  imports: [RmsModule],
  templateUrl: './group-analysis.component.html',
  styleUrl: './group-analysis.component.scss'
})
export class GroupAnalysisComponent implements OnInit, OnDestroy, OnChanges {
  private refresh = new EwsRefresh();
  private liveSubscription?: Subscription;
  UpdatedAt: Date | null = null;
  private initialized = false;
  private BaseData: EwsGroupAnalysisResult | null = null;
  Simulations: EwsSimulation[] = [];
  SkuOptions: any[] = [];
  AddedSku: number | null = null;
  ShowPurchaseSheet = false;
  SimulationMonthOffset = 0;
  SimulationSites = [{ id: null as number | null, label: 'ALL · unallocated' }, { id: 37, label: 'NJ / NJX' }, { id: 31, label: 'WI' }, { id: 45, label: 'FL' }];
  ngOnChanges(changes: SimpleChanges) {
    if (!this.initialized || this.pulldownData) return;
    const changedGroup = JSON.stringify(this.GroupIDs) !== JSON.stringify(this.groupIds);
    this.GroupIDs = [...this.groupIds];
    this.Filters = { ...this.filters };
    if (changedGroup) {
      this.ExcludedCustomers = []; this.ExcludedSkus = []; this.IncludedSkus = [];
      this.Simulations = []; this.NewNoteText = ''; this.NewNoteFollowUp = null;
      this.SimulationMonthOffset = 0;
      this.SelectedWh = null; this.Loaded = false;
      this.loadNotes();
    }
    this.queueRefresh();
  }
  queueRefresh() { this.refresh.schedule(() => this.load()); }
  ngOnDestroy() { this.refresh.destroy(); this.liveSubscription?.unsubscribe(); }

  /** Embedded use (C2.2 screen) — ignored when opened as a pulldown */
  @Input() groupIds: number[] = [];
  @Input() filters: EwsFilters = {};
  /** optional heading, e.g. "Custom group: Brazil blend" — the group names then show underneath */
  @Input() reportTitle = '';

  Loading = false;
  Loaded = false;
  Saving = false;

  GroupIDs: number[] = [];
  Filters: EwsFilters = {};
  BurnRangeOptions = [
    { value: 3, label: '3 months' },
    { value: 6, label: '6 months' },
    { value: 12, label: '12 months' }
  ];

  Data: EwsGroupAnalysisResult | null = null;
  Header: any = null;

  // ---- legacy exclusions / inclusions (re-run on every change) ----
  ExcludedCustomers: { id: number; name: string }[] = [];
  ExcludedSkus: { id: number; name: string }[] = [];
  IncludedSkus: { id: number; name: string }[] = [];

  // ---- warehouse picker + month grid ----
  Warehouses: any[] = [];
  SelectedWh: any = null;            // a row of Warehouses (WarehouseID null = all)
  Waterfall: any[] = [];             // months of the selected warehouse

  // ---- chart (SVG, viewBox units) ----
  readonly ChartW = 1000;
  readonly ChartH = 240;
  readonly ChartPadL = 56;
  readonly ChartPadB = 26;
  readonly ChartPadT = 18;
  ChartBars: ChartBar[] = [];
  ChartZeroY = 0;
  ChartTicks: { y: number; label: string }[] = [];
  ChartGoalY: number | null = null;
  ArrivalPoints = '';                                   // polyline "x,y x,y …"
  ArrivalDots: { x: number; y: number; value: number; label: string }[] = [];

  // ---- tabs ----
  Customers: any[] = [];
  CustomerColumns: Column[] = [];
  TopBurners: any[] = [];
  Skus: any[] = [];
  SkuColumns: Column[] = [];
  SkuTotals: any = null;
  Arrivals: any[] = [];
  ArrivalColumns: Column[] = [];
  Freight: any[] = [];
  FreightColumns: Column[] = [];
  BurnByYear: any[] = [];

  CustomerButtons: HeaderButton[] = [{ label: 'Export CSV', onAction: () => downloadCsv(this.Customers, this.fileName('customers')) }];
  SkuButtons: HeaderButton[] = [{ label: 'Export CSV', onAction: () => downloadCsv(this.Skus, this.fileName('skus')) }];
  ArrivalButtons: HeaderButton[] = [{ label: 'Export CSV', onAction: () => downloadCsv(this.Arrivals, this.fileName('arrivals')) }];

  // ---- notes ----
  Notes: any[] = [];
  NewNoteText = '';
  NewNoteType = 'General';
  NewNoteFollowUp: string | null = null;
  NoteTypeOptions = ['General', 'Buying', 'Pricing', 'Logistics', 'Quality', 'Customer'].map(v => ({ value: v, label: v }));
  SavingNote = false;

  constructor(
    private ews: EwsDataService,
    private msg: RmsMessageService,
    private cdr: ChangeDetectorRef,
    @Optional() private pulldownRef: RmsPulldownRef | null,
    @Optional() @Inject(RMS_PULLDOWN_DATA) private pulldownData: GroupAnalysisData | null
  ) { }

  /** true when shown inside the EWS Group Analysis screen (not a pulldown) */
  get Embedded(): boolean {
    return !this.pulldownData;
  }

  async ngOnInit() {
    const src = this.pulldownData ?? { GroupIDs: this.groupIds, Filters: this.filters };
    this.GroupIDs = [...(src.GroupIDs ?? [])];
    this.Filters = { ...(src.Filters ?? {}) };
    this.initialized = true;
    this.liveSubscription = this.ews.changes$.subscribe(() => this.queueRefresh());
    this.ews.getSkus().subscribe({ next: rows => { this.SkuOptions = rows; this.cdr.markForCheck(); }, error: () => this.msg.error('Could not load the SKU picker.') });
    await this.load();
    await this.loadNotes();
  }

  /** Notes belong to one group — only when exactly one group is open */
  get SingleGroupID(): number | null {
    return this.GroupIDs.length === 1 ? this.GroupIDs[0] : null;
  }

  get HasExclusions(): boolean {
    return this.ExcludedCustomers.length + this.ExcludedSkus.length + this.IncludedSkus.length > 0;
  }

  // ---------------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------------

  async load() {
    const revision = this.refresh.begin();
    this.Loading = true;
    this.cdr.markForCheck();
    try {
      // exclusions / inclusions always go with the run
      this.Filters.ExcludeCustomers = this.ExcludedCustomers.map(c => c.id);
      this.Filters.ExcludeSKUs = this.ExcludedSkus.map(s => s.id);
      this.Filters.IncludeSKUs = this.IncludedSkus.map(s => s.id);

      const d = await lastValueFrom(this.ews.getGroupAnalysis(this.GroupIDs, this.Filters));
      if (!this.refresh.current(revision)) return;
      this.BaseData = d;
      this.applySimulation();

      this.buildCustomers(d);
      this.buildSkus(d);
      this.buildArrivals(d);
      this.buildFreight(d);
      this.BurnByYear = d?.BurnByYear ?? [];
      this.Loaded = true;
      this.UpdatedAt = new Date();
    } catch (err: any) {
      if (this.refresh.current(revision)) this.msg.error(err?.error?.error ?? 'Could not load the group analysis.');
    } finally {
      if (!this.refresh.current(revision)) return;
      this.Loading = false;
      this.cdr.markForCheck();
    }
  }

  get SimulationMonths(): any[] { return (this.BaseData?.Waterfall ?? []).filter(m => m.WarehouseID == null); }
  get VisibleSimulationMonths(): any[] { return this.SimulationMonths.slice(this.SimulationMonthOffset, this.SimulationMonthOffset + 12); }
  changeSimulationMonths(direction: number) {
    this.SimulationMonthOffset = Math.max(0, Math.min(Math.max(0, Math.ceil(this.SimulationMonths.length / 12) - 1) * 12, this.SimulationMonthOffset + direction * 12));
  }
  get SimulationTotal(): number { return this.Simulations.reduce((n,s) => n+s.Bags,0); }
  simulationValue(warehouse: number | null, date: string): number | null {
    return this.Simulations.find(s => s.WarehouseID === warehouse && dateOnly(s.MonthStart) === dateOnly(date))?.Bags ?? null;
  }
  editSimulation(warehouse: number | null, date: string, value: any) {
    const bags = value === '' || value == null ? 0 : Number(value);
    if (!Number.isFinite(bags) || !Number.isInteger(bags) || bags < 0 || bags > 9999999) {
      this.msg.warn('Enter a whole number of bags between 0 and 9,999,999.'); return;
    }
    this.Simulations = this.Simulations.filter(s => !(s.WarehouseID === warehouse && dateOnly(s.MonthStart) === dateOnly(date)));
    if (bags) this.Simulations = [...this.Simulations, { WarehouseID: warehouse, MonthStart: dateOnly(date)!, Bags: bags }];
    this.applySimulation();
  }
  clearSimulations() { this.Simulations = []; this.applySimulation(); }
  private applySimulation() {
    if (!this.BaseData) return;
    this.Data = simulateEws(this.BaseData, this.Simulations);
    this.Header = this.Data?.Header;
    const keep = this.SelectedWh?._key ?? 'ALL';
    this.Warehouses = (this.Data?.Warehouses ?? []).map(w => ({...w, _key: whKey(w.WarehouseID)}));
    this.selectWarehouse(this.Warehouses.find(w => w._key === keep) ?? this.Warehouses[0] ?? null);
  }
  addSku() {
    const id = Number(this.AddedSku), sku = this.SkuOptions.find(s => s.InventoryTypeID === id);
    if (!sku || this.Loading) return;
    this.ExcludedSkus = this.ExcludedSkus.filter(s => s.id !== id);
    if (!this.IncludedSkus.some(s => s.id === id)) this.IncludedSkus = [...this.IncludedSkus, {id, name: sku.InventoryTypeName}];
    this.AddedSku = null;
    this.load();
  }
  exportPurchaseSheet() {
    downloadCsv((this.Data?.Waterfall ?? []).map(m => ({
      Group: this.Header?.GroupNames,
      Warehouse: this.Warehouses.find(w => w._key === whKey(m.WarehouseID))?.WarehouseName,
      Month: dateOnly(m.MonthStart), Opening: m.OpeningBags, SoldArrivals: m.ArrivalsSold,
      UnsoldArrivals: m.ArrivalsUnsold, SimulatedPurchases: m.SimulationBags || 0,
      Goal: m.GoalBags, Closing: m.ClosingBags
    })), this.fileName('purchase-status'));
  }

  selectWarehouse(w: any) {
    this.SelectedWh = w;
    const key = w?._key ?? 'ALL';
    this.Waterfall = (this.Data?.Waterfall ?? [])
      .filter(m => whKey(m.WarehouseID) === key)
      .map(m => ({
        ...m,
        Label: monthLabel(m.MonthStart),
        Opening: round0(m.OpeningBags),
        Arriving: round0((m.ArrivalsSold ?? 0) + (m.ArrivalsUnsold ?? 0) + (m.SimulationBags ?? 0)),
        ArrSold: round0(m.ArrivalsSold),
        ArrUnsold: round0(m.ArrivalsUnsold),
        Goal: round1(m.GoalBags),
        Closing: round0(m.ClosingBags),
        IsCrop: !!(m.IsCrop1Month || m.IsCrop2Month),
        CropLabel: m.IsCrop1Month ? 'Crop #1' : m.IsCrop2Month ? 'Crop #2' : ''
      }));
    this.buildChart();
    this.cdr.markForCheck();
  }

  // ---------------------------------------------------------------------
  // Legacy ✗ / ✓ — exclude a customer, exclude / include a SKU, re-run
  // ---------------------------------------------------------------------

  excludeCustomer(row: any) {
    if (this.Loading) return;   // one re-run at a time
    if (this.ExcludedCustomers.some(c => c.id === row.CompanyID)) return;
    this.ExcludedCustomers = [...this.ExcludedCustomers, { id: row.CompanyID, name: row.CompanyName }];
    this.load();
  }

  restoreCustomer(id: number) {
    this.ExcludedCustomers = this.ExcludedCustomers.filter(c => c.id !== id);
    this.load();
  }

  /** ✗ on an Active / Included SKU · ✓ on a Not active / Excluded SKU */
  toggleSku(row: any) {
    if (this.Loading) return;   // one re-run at a time
    const id = row.InventoryTypeID;
    const name = row.InventoryTypeName;
    switch (row.SkuStatus) {
      case 'Active': this.ExcludedSkus = [...this.ExcludedSkus, { id, name }]; break;
      case 'Included': this.IncludedSkus = this.IncludedSkus.filter(s => s.id !== id); break;
      case 'Not active': this.IncludedSkus = [...this.IncludedSkus, { id, name }]; break;
      case 'Excluded': this.ExcludedSkus = this.ExcludedSkus.filter(s => s.id !== id); break;
    }
    this.load();
  }

  restoreSku(id: number, list: 'excluded' | 'included') {
    if (list === 'excluded') this.ExcludedSkus = this.ExcludedSkus.filter(s => s.id !== id);
    else this.IncludedSkus = this.IncludedSkus.filter(s => s.id !== id);
    this.load();
  }

  clearExclusions() {
    this.ExcludedCustomers = [];
    this.ExcludedSkus = [];
    this.load();
  }

  clearSkuExclusions() {
    this.ExcludedSkus = [];
    this.load();
  }

  clearInclusions() {
    this.IncludedSkus = [];
    this.load();
  }

  getCustomerIcons = (row: any): TableIcon[] => [
    { icon: 'close', title: 'Exclude this customer and re-run', action: () => this.excludeCustomer(row) }
  ];

  getSkuIcons = (row: any): TableIcon[] => {
    const inAnalysis = row.SkuStatus === 'Active' || row.SkuStatus === 'Included';
    return [{
      icon: inAnalysis ? 'close' : 'check',
      title: row.SkuStatus === 'Active' ? 'Exclude this SKU and re-run'
        : row.SkuStatus === 'Included' ? 'Remove the manual inclusion and re-run'
          : row.SkuStatus === 'Not active' ? 'Include this SKU and re-run'
            : 'Undo the exclusion and re-run',
      action: () => this.toggleSku(row)
    }];
  };

  // ---------------------------------------------------------------------
  // Chart — bars = bags left at month end (red when short), yellow line =
  // bags arriving that month (legacy), gold dashed line at the new-crop
  // month(s) with the value there, dotted line = one month's goal.
  // One axis: everything is bags.
  // ---------------------------------------------------------------------

  private buildChart() {
    const months = this.Waterfall;
    if (!months.length) {
      this.ChartBars = []; this.ChartTicks = []; this.ArrivalPoints = ''; this.ArrivalDots = [];
      return;
    }

    const values = months.map(m => m.Closing ?? 0);
    const arrivals = months.map(m => m.Arriving ?? 0);
    const goal = this.SelectedWh?.GoalBags ? Number(this.SelectedWh.GoalBags) : 0;
    let max = Math.max(0, ...values, ...arrivals, goal);
    let min = Math.min(0, ...values);
    if (max === min) max = min + 1;
    const pad = (max - min) * 0.08;
    max += pad;
    if (min < 0) min -= pad;

    const plotTop = this.ChartPadT;
    const plotH = this.ChartH - this.ChartPadB - plotTop;
    const plotW = this.ChartW - this.ChartPadL - 8;
    const yOf = (v: number) => plotTop + (max - v) / (max - min) * plotH;
    const slot = plotW / months.length;
    const barW = Math.max(4, slot * 0.62);

    this.ChartZeroY = yOf(0);
    this.ChartGoalY = goal > 0 ? yOf(goal) : null;
    this.ChartBars = months.map((m, i) => {
      const v = m.Closing ?? 0;
      const yTop = v >= 0 ? yOf(v) : this.ChartZeroY;
      const h = Math.max(1, Math.abs(yOf(v) - this.ChartZeroY));
      return {
        x: this.ChartPadL + i * slot + (slot - barW) / 2,
        y: yTop, w: barW, h,
        value: v, label: m.Label, short: v < 0, crop: m.IsCrop, cropLabel: m.CropLabel
      };
    });

    // arrivals line — a dot at the centre of each month
    this.ArrivalDots = months.map((m, i) => ({
      x: this.ChartPadL + i * slot + slot / 2,
      y: yOf(m.Arriving ?? 0),
      value: m.Arriving ?? 0,
      label: m.Label
    }));
    this.ArrivalPoints = this.ArrivalDots.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

    // gridlines with round-ish labels
    const step = niceStep((max - min) / 4);
    const ticks: { y: number; label: string }[] = [];
    for (let t = Math.ceil(min / step) * step; t <= max; t += step) {
      ticks.push({ y: yOf(t), label: fmt(t) });
    }
    this.ChartTicks = ticks;
  }

  // ---------------------------------------------------------------------
  // Tabs
  // ---------------------------------------------------------------------

  private buildCustomers(d: EwsGroupAnalysisResult) {
    // pickups by month (last 12 months of the burn window) → P1…P12 columns
    const monthStarts = [...new Set((d?.CustomerMonths ?? []).map(m => dateOnly(m.MonthStart)!))].sort();
    const byCust = new Map<number, Record<string, number>>();
    for (const m of d?.CustomerMonths ?? []) {
      if (!byCust.has(m.CompanyID)) byCust.set(m.CompanyID, {});
      byCust.get(m.CompanyID)![dateOnly(m.MonthStart)!] = m.Bags;
    }

    this.Customers = (d?.Customers ?? []).map(c => {
      const row: any = {
        ...c,
        Burn3: round1(c.Burn3), Burn6: round1(c.Burn6), Burn12: round1(c.Burn12), BurnUsed: round1(c.BurnUsed),
        ContractThru: dateOnly(c.ContractThru), LastPickup: dateOnly(c.LastPickup), EstDepletion: dateOnly(c.EstDepletion),
        TopFlag: c.IsTop ? 'Top ' + c.BurnRank : ''
      };
      const mm = byCust.get(c.CompanyID) ?? {};
      monthStarts.forEach((ms, i) => row[`P${i + 1}`] = mm[ms] ?? 0);
      return row;
    });
    this.TopBurners = this.Customers.filter(c => c.IsTop);

    if (!this.CustomerColumns.length) {
      this.CustomerColumns = [
        { key: 'BurnRank', label: 'Rank', type: 'number', hidden: false, width: 4 },
        { key: 'CompanyName', label: 'Customer', hidden: false, align: 'left', width: 16 },
        { key: 'TopFlag', label: 'Top Burner', type: 'select', SearchType: 'select', hidden: false },
        { key: 'Burn3', label: '3 Month', type: 'number', hidden: false },
        { key: 'Burn6', label: '6 Month', type: 'number', hidden: false },
        { key: 'Burn12', label: '12 Month', type: 'number', hidden: false },
        { key: 'PctOfBurn', label: '% of Burn', type: 'number', fixed: 1, hidden: false },
        { key: 'ReservedSpot', label: 'Reserved Spot', type: 'number', hidden: true },
        { key: 'ReservedForward', label: 'Reserved Fwd', type: 'number', hidden: true },
        { key: 'OnReserve', label: 'On Reserve', type: 'number', hidden: false },
        { key: 'ContractThru', label: 'Contract Thru', type: 'date', SearchType: 'date', hidden: false },
        { key: 'EstDepletion', label: 'Est. Depletion', type: 'date', SearchType: 'date', hidden: false },
        { key: 'ReserveMonths', label: 'Reserve Months', type: 'number', fixed: 1, hidden: false },
        { key: 'LastPickup', label: 'Last Pickup', type: 'date', SearchType: 'date', hidden: false },
        { key: 'ReserveOrders', label: 'Reserves', type: 'number', hidden: true },
        { key: 'CompanyID', label: 'Company #', type: 'number', hidden: true },
        // monthly pickups — last 6 shown, older 6 via customize
        ...monthStarts.map((ms, i) => ({
          key: `P${i + 1}`, label: monthLabel(ms), type: 'number' as const,
          hidden: i < monthStarts.length - 6
        }))
      ];
    }
  }

  private buildSkus(d: EwsGroupAnalysisResult) {
    this.Skus = (d?.Skus ?? []).map(s => ({
      ...s,
      BurnUsed: round1(s.BurnUsed), Burn3: round1(s.Burn3), Burn6: round1(s.Burn6), Burn12: round1(s.Burn12),
      SpotOnHand: round0(s.SpotOnHand), SpotSold: round0(s.SpotSold), SpotUnsold: round0(s.SpotUnsold),
      ForwardBags: round0(s.ForwardBags), ForwardSold: round0(s.ForwardSold), ForwardUnsold: round0(s.ForwardUnsold),
      NextArrival: dateOnly(s.NextArrival), EstDepletionMonth: dateOnly(s.EstDepletionMonth),
      CropArrival: dateOnly(s.CropArrival), LastArrival: dateOnly(s.LastArrival), LastPickup: dateOnly(s.LastPickup),
      ExportWindow: exportWindow(s.ExportStartMonth, s.ExportEndMonth)
    }));

    // legacy "GRAND TOTALS" — SKUs in the analysis only
    const inA = this.Skus.filter(s => s.IsActive);
    const sum = (k: string) => inA.reduce((t, s) => t + (Number(s[k]) || 0), 0);
    this.SkuTotals = {
      Count: inA.length,
      BurnUsed: Math.round(sum('BurnUsed') * 10) / 10,
      SpotSold: sum('SpotSold'), SpotUnsold: sum('SpotUnsold'),
      ForwardSold: sum('ForwardSold'), ForwardUnsold: sum('ForwardUnsold'),
      OnReserve: sum('OnReserve')
    };

    if (!this.SkuColumns.length) {
      this.SkuColumns = [
        {
          key: 'SkuStatus', label: 'Status', type: 'select', SearchType: 'select', hidden: false, width: 7,
          cellClass: (r: any) => skuStatusClass(r.SkuStatus)
        },
        { key: 'InventoryTypeID', label: 'SKU #', type: 'number', hidden: false, width: 5 },
        { key: 'InventoryTypeName', label: 'SKU Name', hidden: false, align: 'left', width: 20 },
        { key: 'GroupName', label: 'Group', type: 'select', SearchType: 'select', hidden: true },
        { key: 'BurnUsed', label: 'Burn / Mo', type: 'number', hidden: false },
        { key: 'SpotSold', label: 'In Store Sold', type: 'number', hidden: false },
        { key: 'SpotUnsold', label: 'In Store Unsold', type: 'number', hidden: false },
        { key: 'ForwardSold', label: 'Forward Sold', type: 'number', hidden: false },
        { key: 'ForwardUnsold', label: 'Forward Unsold', type: 'number', hidden: false },
        { key: 'OnReserve', label: 'On Reserve', type: 'number', hidden: true },
        { key: 'EstDepletionMonth', label: 'Est. Dep', type: 'date', SearchType: 'date', hidden: false },
        { key: 'LastArrival', label: 'Last Arrived', type: 'date', SearchType: 'date', hidden: false },
        { key: 'NextArrival', label: 'Next Arrival', type: 'date', SearchType: 'date', hidden: true },
        { key: 'CropArrival', label: 'New Crop', type: 'date', SearchType: 'date', hidden: true },
        { key: 'ExportWindow', label: 'Export Window', hidden: true },
        { key: 'LastPickup', label: 'Last Pickup', type: 'date', SearchType: 'date', hidden: true },
        { key: 'Burn3', label: 'Burn 3', type: 'number', hidden: true },
        { key: 'Burn6', label: 'Burn 6', type: 'number', hidden: true },
        { key: 'Burn12', label: 'Burn 12', type: 'number', hidden: true },
        { key: 'SpotOnHand', label: 'Spot On Hand', type: 'number', hidden: true },
        { key: 'ForwardBags', label: 'Fwd Bags', type: 'number', hidden: true },
      ];
    }
  }

  private buildArrivals(d: EwsGroupAnalysisResult) {
    this.Arrivals = (d?.ForwardLots ?? []).map(l => ({
      ...l,
      EstArrival: dateOnly(l.EstArrival),
      Confirmed: l.IsConfirmed ? 'Yes' : 'No',
      PastDue: l.IsPastDue ? 'Past due' : ''
    }));

    if (!this.ArrivalColumns.length) {
      this.ArrivalColumns = [
        { key: 'EstArrival', label: 'Est. Arrival', type: 'date', SearchType: 'date', hidden: false },
        { key: 'ArrivalStatus', label: 'Status', type: 'select', SearchType: 'select', hidden: false },
        { key: 'Confirmed', label: 'Confirmed', type: 'select', SearchType: 'select', hidden: false },
        {
          key: 'PastDue', label: 'Past Due', type: 'select', SearchType: 'select', hidden: false,
          cellClass: (r: any) => (r.IsPastDue ? 'ga-neg-text' : '')
        },
        { key: 'InventoryID', label: 'Lot #', type: 'number', hidden: false },
        { key: 'InventoryTypeName', label: 'Coffee SKU', hidden: false, align: 'left', width: 20 },
        { key: 'WarehouseName', label: 'Warehouse', type: 'select', SearchType: 'select', hidden: false },
        { key: 'FinalPosition', label: 'Position', hidden: false },
        { key: 'OnHandBags', label: 'Bags', type: 'number', hidden: false },
        { key: 'ReservedBags', label: 'Reserved', type: 'number', hidden: false },
        { key: 'PendingBags', label: 'On Orders', type: 'number', hidden: true },
        { key: 'SoldBags', label: 'Sold', type: 'number', hidden: false },
        { key: 'UnsoldBags', label: 'Unsold', type: 'number', hidden: false },
      ];
    }
  }

  private buildFreight(d: EwsGroupAnalysisResult) {
    this.Freight = (d?.FreightSamples ?? []).map(f => ({
      ...f, StartDate: dateOnly(f.StartDate), DateIn: dateOnly(f.DateIn)
    }));

    if (!this.FreightColumns.length) {
      this.FreightColumns = [
        { key: 'InventoryID', label: 'Ref #', type: 'number', hidden: false },
        { key: 'InventoryTypeName', label: 'SKU', hidden: false, align: 'left', width: 24 },
        { key: 'Bags', label: '# of Bags', type: 'number', hidden: false },
        { key: 'WarehouseName', label: 'Destn', hidden: false },
        { key: 'FinalPosition', label: 'Purchase Position', hidden: false },
        { key: 'StartType', label: 'From', hidden: false },
        { key: 'StartDate', label: 'ETD / Afloat', type: 'date', SearchType: 'date', hidden: false },
        { key: 'DateIn', label: 'Date In', type: 'date', SearchType: 'date', hidden: false },
        { key: 'Days', label: 'Days to INSTORE', type: 'number', hidden: false },
        { key: 'CountryName', label: 'Country', hidden: true },
      ];
    }
  }

  // ---------------------------------------------------------------------
  // Notes (one group only)
  // ---------------------------------------------------------------------

  async loadNotes() {
    if (!this.SingleGroupID) { this.Notes = []; return; }
    try {
      this.Notes = await lastValueFrom(this.ews.getNotes({ NoteScope: 'EWS', GroupID: this.SingleGroupID })) ?? [];
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not load notes.');
    } finally {
      this.cdr.markForCheck();
    }
  }

  async addNote() {
    const text = (this.NewNoteText ?? '').trim();
    if (!text || !this.SingleGroupID) return;
    this.SavingNote = true;
    try {
      await lastValueFrom(this.ews.saveNote({
        NoteScope: 'EWS',
        GroupID: this.SingleGroupID,
        NoteType: this.NewNoteType,
        NoteText: text,
        FollowUpDate: this.NewNoteFollowUp || null
      }));
      this.NewNoteText = '';
      this.NewNoteFollowUp = null;
      await this.loadNotes();
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not save the note.');
    } finally {
      this.SavingNote = false;
      this.cdr.markForCheck();
    }
  }

  async deleteNote(note: any) {
    if (!confirm('Delete this note?')) return;
    try {
      await lastValueFrom(this.ews.deleteNote(note.NoteID));
      await this.loadNotes();
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not delete the note.');
    }
  }

  // ---------------------------------------------------------------------
  // Save forecast — header + every warehouse's months as a new version
  // ---------------------------------------------------------------------

  async saveForecast() {
    if (!this.Data || !this.Header) return;
    const name = prompt('Name for this saved forecast:', `${this.Header.GroupNames} — ${new Date().toISOString().substring(0, 10)}`);
    if (name === null) return;   // cancelled

    const lines: ForecastLine[] = [];
    const groupId = this.SingleGroupID;
    // "Other" locations (WarehouseID -1: Lineup, SANROKE, #57 …) are not real warehouses, so they can't be
    // saved as their own lines (FK to dbo.location). Their bags are already inside "All warehouses".
    const realWh = (id: any) => id !== -1;

    for (const w of this.Data.Warehouses.filter(x => realWh(x.WarehouseID))) {
      lines.push({
        LineType: 'SUMMARY', GroupID: groupId, WarehouseID: w.WarehouseID,
        BurnRate: w.BurnUsed, GoalBags: w.GoalBags, OpeningBags: w.SpotOnHand,
        ReservedBags: w.SpotReserved, ArrivalsUnsoldBags: w.ForwardUnsold,
        LineNote: `${w.WarehouseName}: ${w.CoverageStatus}; ${w.ActionNeeded}`
      });
    }
    for (const m of this.Data.Waterfall.filter(x => realWh(x.WarehouseID))) {
      lines.push({
        LineType: 'MONTH', GroupID: groupId, WarehouseID: m.WarehouseID,
        MonthStart: dateOnly(m.MonthStart), GoalBags: m.GoalBags, OpeningBags: m.OpeningBags,
        ArrivalsSoldBags: m.ArrivalsSold, ArrivalsUnsoldBags: m.ArrivalsUnsold, ProposedBags: m.SimulationBags || 0, ClosingBags: m.ClosingBags
      });
    }

    this.Saving = true;
    try {
      const r = await lastValueFrom(this.ews.saveRun({
        RunType: 'EWS',
        RunName: name.trim() || null,
        AsOfDate: dateOnly(this.Header.BurnWindowEnd),
        BurnRange: this.Header.BurnRange,
        SafsValue: this.Header.SafsValue,
        SafsType: this.Header.SafsType,
        CropOption: this.Header.CropOption,
        VolatileMarket: !!this.Header.VolatileMarket,
        ExcludeFireSales: !!this.Header.ExcludeFireSales,
        DaysInAdj: this.Header.DaysInAdj,
        GroupIDs: this.GroupIDs,
        ExcludeCustomers: this.ExcludedCustomers.map(c => c.id),
        IncludeSKUs: this.IncludedSkus.map(s => s.id),
        ExcludeSKUs: this.ExcludedSkus.map(s => s.id),
        Assumptions: {
          Simulations: this.Simulations,
          ForecastDate: dateOnly(this.Header.ForecastDate),
          IsTestMode: !!this.Header.IsTestMode,
          Crop1Arrival: dateOnly(this.Header.Crop1Arrival),
          Crop2Arrival: dateOnly(this.Header.Crop2Arrival),
          AvgDaysToWarehouse: this.Header.AvgDaysToWarehouse,
          SkusInAnalysis: this.Header.SkusInAnalysis
        },
        Lines: lines
      }));
      this.msg.success(`Forecast saved as version ${r.VersionNo}.`);
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not save the forecast.');
    } finally {
      this.Saving = false;
      this.cdr.markForCheck();
    }
  }

  close() {
    this.pulldownRef?.close();
  }

  // ---------------------------------------------------------------------
  // Template helpers
  // ---------------------------------------------------------------------

  coverageClass(status: string): string {
    switch (status) {
      case 'Immediate Action': return 'ga-neg';
      case 'Purchase Soon': return 'ga-warn';
      case 'Watch List': return 'ga-watch';
      case 'Covered': return 'ga-ok';
      default: return 'ga-none';
    }
  }

  /** legacy header: "Export Window: AUG '27 – MAR '27" style, upper case */
  get ExportWindowText(): string {
    return exportWindow(this.Header?.ExportStartMonth, this.Header?.ExportEndMonth).toUpperCase() || '—';
  }

  /** The header band follows the warehouse picked (All warehouses = group header) */
  get Band(): any {
    return this.SelectedWh ?? this.Header;
  }

  /** "Spot & Forward Depletion: Oct '26" — first month short (spot + arrivals) */
  get DepletionText(): string {
    const d = this.Band?.DepletionMonth;
    return d ? monthLabel(d) : 'none in ' + (this.Header?.ForecastMonths ?? 24) + ' months';
  }

  /** "Spot Depletion: Aug '26" — spot only, no arrivals */
  get SpotDepletionText(): string {
    const d = this.Band?.SpotDepletionMonth;
    return d ? monthLabel(d) : '—';
  }

  get MtdChangePct(): number | null {
    const now = this.Header?.MtdBags ?? 0;
    const last = this.Header?.MtdLastYearBags ?? 0;
    return last > 0 ? Math.round((now - last) * 1000 / last) / 10 : null;
  }

  d(v: any): string | null { return dateOnly(v); }
  m(v: any): string { return monthLabel(v); }

  private fileName(part: string): string {
    const ids = this.GroupIDs.join('-');
    return `ews-group-${ids}-${part}-${new Date().toISOString().substring(0, 10)}.csv`;
  }
}

// ---------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------

/** Warehouse key: null (all) → 'ALL', otherwise the id as text */
function whKey(id: any): string {
  return id === null || id === undefined ? 'ALL' : String(id);
}

function round0(v: any): number | null {
  return v === null || v === undefined ? null : Math.round(Number(v));
}

function round1(v: any): number | null {
  return v === null || v === undefined ? null : Math.round(Number(v) * 10) / 10;
}

function fmt(v: number): string {
  return Math.round(v).toLocaleString('en-US');
}

function niceStep(raw: number): number {
  if (raw <= 0) return 1;
  const p = Math.pow(10, Math.floor(Math.log10(raw)));
  const n = raw / p;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * p;
}

function exportWindow(start: any, end: any): string {
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const s = Number(start), e = Number(end);
  if (!s) return '';
  return e ? `${names[s - 1]} – ${names[e - 1]}` : names[s - 1];
}

function skuStatusClass(status: string): string {
  switch (status) {
    case 'Active': return 'ga-ok-text';
    case 'Included': return 'ga-incl-text';
    case 'Excluded': return 'ga-neg-text';
    default: return 'ga-muted-text';
  }
}
